// FS Audited (Round) 模式下的四舍五入差值调整 API
import { BASE_AUDIT_API, fetchAPI } from '@/services/api';

/** targets 元素，与后端 AuditRoundingAdjustTargetVO 一致 */
export interface RoundingAdjustTarget {
    targetFsId: number;
    targetCode: string;
    targetStatement: 'BS' | 'PL';
    adjustAmount: number;
    columnType: number;
    /**
     * 子期间 YOA；SINGLE / BS / legacy 为 null 或省略。
     * MULTI·PL 为单片 YOA（如 2021/22）。
     */
    yearOfAssessment?: string | null;
}

/** list / save 统一聚合结构，与后端 AuditRoundingAdjustGroupVO 一致 */
export interface RoundingAdjustGroupVO {
    id?: number | null;
    annualId: number;
    targets: RoundingAdjustTarget[];
}

export interface RoundingAdjustTargetBO {
    targetFsId: number;
    targetCode: string;
    targetStatement: 'BS' | 'PL';
    adjustAmount: number;
    /** MULTI·PL 必填；SINGLE / BS 不传或 null */
    yearOfAssessment?: string | null;
}

export interface RoundingAdjustSaveBO {
    annualId: number;
    columnType?: number;
    targets: RoundingAdjustTargetBO[];
}

export function emptyRoundingAdjustGroup(annualId: number): RoundingAdjustGroupVO {
    return {annualId, targets: []};
}

function normalizeYoa(value: unknown): string | null {
    if (value == null) return null;
    const s = String(value).trim();
    return s ? s : null;
}

function mapTargetRow(row: Record<string, unknown>): RoundingAdjustTarget {
    return {
        targetFsId: Number(row.targetFsId),
        targetCode: String(row.targetCode || ''),
        targetStatement: row.targetStatement as 'BS' | 'PL',
        adjustAmount: Number(row.adjustAmount) || 0,
        columnType: Number(row.columnType) || 1,
        yearOfAssessment: normalizeYoa(row.yearOfAssessment),
    };
}

/** 将后端 data 规范为聚合结构（含 MULTI YOA targets） */
export function normalizeRoundingAdjustGroup(
    data: unknown,
    annualId: number
): RoundingAdjustGroupVO {
    if (data == null) return emptyRoundingAdjustGroup(annualId);

    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj.targets)) {
        return {
            id: obj.id != null ? Number(obj.id) : undefined,
            annualId: Number(obj.annualId) || annualId,
            targets: (obj.targets as Record<string, unknown>[]).map(mapTargetRow),
        };
    }

    return emptyRoundingAdjustGroup(annualId);
}

export const roundingAdjustService = {
    query: async (annualId: number): Promise<RoundingAdjustGroupVO> => {
        const raw = await fetchAPI<unknown>(
            `${BASE_AUDIT_API}/rounding-adjust/list?annualId=${annualId}`
        );
        return normalizeRoundingAdjustGroup(raw, annualId);
    },

    save: async (bo: RoundingAdjustSaveBO): Promise<RoundingAdjustGroupVO> => {
        const raw = await fetchAPI<unknown>(`${BASE_AUDIT_API}/rounding-adjust/save`, {
            method: 'POST',
            body: JSON.stringify(bo),
        });
        return normalizeRoundingAdjustGroup(raw, bo.annualId);
    },

    remove: (annualId: number, columnType?: number): Promise<void> => {
        const params = new URLSearchParams({annualId: String(annualId)});
        if (columnType != null) params.set('columnType', String(columnType));
        return fetchAPI(`${BASE_AUDIT_API}/rounding-adjust/remove?${params.toString()}`, {
            method: 'DELETE',
        });
    },
};
